import { Link } from "react-router-dom";
import { BookOpen, ArrowDown, Book, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/shared/Header";
import { LearningBreadcrumbs } from "@/components/learning/LearningBreadcrumbs";

const HumanInquiry = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <LearningBreadcrumbs 
        disciplineName="The Human Inquiry"
        // TODO: Add real learning path data
        showProgress={false}
        compact={true}
      />

      {/* Hero Section */}
      <section className="py-20 px-6 bg-whiter from-primary/5 to-muted/20">
        <div className="container mx-auto text-center max-w-4xl">
          <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-8">
            <BookOpen className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-5xl font-bold mb-6 bg-white from-primary to-primary/70 bg-clip-text text-transparent">
            The Human Inquiry
          </h1>
          <p className="text-xl text-black leading-relaxed mb-8">
            All disciplines born from the inner quest for meaning, identity, truth, and justice. 
            Here we explore the deepest questions of existence and the highest aspirations of human thought.
          </p>
          <ArrowDown className="mx-auto w-6 h-6 text-black animate-bounce" />
        </div>
      </section>

      {/* Disciplines */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid gap-8">
            
            {/* Theology & Church History */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Book className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Theology & Church History</CardTitle>
                    <CardDescription className="text-lg">
                      The study of God, divine revelation, and the historical development of the church
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Theological Studies</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Systematic Theology - Classical doctrines</li>
                      <li>• Biblical Theology - Scripture and covenants</li>
                      <li>• Historical Theology - Patristic to modern</li>
                      <li>• Moral & Practical Theology - Ethics and practice</li>
                      <li>• Comparative Theology - World religions and dialogue</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Church History</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Early Church & Patristic Period</li>
                      <li>• Medieval Church & Scholasticism</li>
                      <li>• Reformation & Counter-Reformation</li>
                      <li>• Modern Church & Missions</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/study/theology-church-history">Explore Theology & Church History</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Philosophy */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white0/10 rounded-full flex items-center justify-center group-hover:bg-white0/20 transition-colors">
                    <BookOpen className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Philosophy</CardTitle>
                    <CardDescription className="text-lg">
                      Love of wisdom and fundamental questions about existence, knowledge, and values
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-black">Core Areas</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Metaphysics & Ontology - Being and reality</li>
                      <li>• Epistemology - Knowledge and truth</li>
                      <li>• Ethics & Moral Philosophy - Values and conduct</li>
                      <li>• Logic & Critical Reasoning - Rational discourse</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-black">Applied Philosophy</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Political & Legal Philosophy</li>
                      <li>• Philosophy of Religion</li>
                      <li>• Philosophy of Science & Mathematics</li>
                      <li>• Aesthetics & Philosophy of Art</li>
                    </ul>
                  </div>
                </div>
                <Button asChild variant="outline">
                  <Link to="/study/philosophy">Explore Philosophy</Link>
                </Button>
              </CardContent>
            </Card>

            {/* History & Law */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <History className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">History & Law</CardTitle>
                    <CardDescription className="text-lg">
                      Mapping the evolution of human societies and their governing ideas
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Historical Studies</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Historical Periods - Ancient to contemporary</li>
                      <li>• Regional & Civilizational History</li>
                      <li>• Thematic & Intellectual History</li>
                      <li>• Cultural & Social History</li>
                      <li>• Historiography & Methodology</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Legal Systems</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Maritime Law</li>
                      <li>• International Law</li>
                      <li>• Constitutional Theory</li>
                      <li>• Rights & Sovereignty</li>
                    </ul>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button asChild>
                    <Link to="/study/history">Explore History</Link>
                  </Button>
                  <Button asChild variant="outline">
                    <Link to="/study/law">Explore Law</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>



          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-16 px-6 bg-muted/30">
        <div className="container mx-auto text-center max-w-4xl">
          <h3 className="text-2xl font-bold mb-6">Continue Your Journey</h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="outline">
              <Link to="/world-itself">Explore The World Itself</Link>
            </Button>
            <Button asChild size="lg">
              <Link to="/convergence">Discover Intersections</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HumanInquiry;

